import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AccountProfileSection } from '../../AccountProfileSection'
import { HappyCloudEntryCard } from '@/src/renderer/components/common/HappyCloudEntryCard'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import { getPlatformAPI } from '@/src/renderer/lib/platform'

interface AccountProfilePageProps {
  /** Navigate to the "HappyCloud Sync" SubNav — wired by the slot registry.
   *  Triggered by the HappyCloud entry card's CTA. */
  onSetupSync: () => void
  /** Where the page runs; defaults to the platform adapter (Storybook passes it explicitly). */
  platform?: 'electron' | 'web'
}

interface SyncStatus {
  /** Logged into a HappyCloud account ("online"). */
  isAuthenticated: boolean
  /** Sync booked/activated on the account (`profiles.sync_entitlement`). */
  hasEntitlement: boolean
}

/**
 * "Profil" page under "Profil & Daten". Hosts the profile section (avatar,
 * display name, email) and a HappyCloud sync entry card.
 *
 * The entry card is Electron-only: the web app is cloud-only (always synced) and
 * gates access behind login + entitlement, so a "set up / connect sync" card has
 * no purpose there — it is never shown on web.
 *
 * On Electron the card state is driven by account auth + the cloud entitlement
 * (NOT the local storage mode):
 * - `entitlement === true` → fully booked → card hidden.
 * - logged in, `entitlement === false` → has an account but hasn't booked →
 *   card shown, CTA "Sync aktivieren".
 * - not logged in → no account yet → card shown, CTA "Einrichten".
 *
 * Both signals are read directly (the preferences window has no SyncProvider/
 * AuthProvider) and kept fresh via the `storage-mode-changed` broadcast — slots
 * stay mounted once visited, so the card must update without a window reopen.
 */
export function AccountProfilePage({ onSetupSync, platform }: AccountProfilePageProps) {
  const { t } = useTranslation()
  const account = useAccountSource()
  // null = still resolving; keep the card hidden meanwhile so an already-booked
  // account never flashes it.
  const [status, setStatus] = useState<SyncStatus | null>(null)

  const loadStatus = useCallback(async (): Promise<SyncStatus> => {
    try {
      const uid = await account.getUserId()
      if (!uid) return { isAuthenticated: false, hasEntitlement: false }
      const entitled = await account.getSyncEntitlement(uid)
      return { isAuthenticated: true, hasEntitlement: entitled }
    } catch {
      return { isAuthenticated: false, hasEntitlement: false }
    }
  }, [account])

  useEffect(() => {
    let cancelled = false
    loadStatus().then((s) => {
      if (!cancelled) setStatus(s)
    })
    return () => {
      cancelled = true
    }
  }, [loadStatus])

  // Live-update across the preferences window. The HappyCloud-Sync toggle lives in
  // a sibling slot (SyncPage); flipping it writes the entitlement and nudges the
  // storage mode, and the main process broadcasts `storage-mode-changed` to every
  // window. Preferences slots stay mounted once visited (lazy-mount + display:none),
  // so the mount read above never re-runs on navigation — we treat the broadcast as
  // a generic "sync settings changed" signal and re-read auth + entitlement.
  useEffect(() => {
    let active = true
    const stopListening = account.onSyncSettingsChanged(() => {
      loadStatus().then((s) => {
        if (active) setStatus(s)
      })
    })
    return () => {
      active = false
      stopListening()
    }
  }, [loadStatus, account])

  // Web is always cloud-synced (and access is already gated on entitlement) → the
  // card never applies there. On Electron, show it until the account is booked.
  const isWeb = (platform ?? getPlatformAPI().platform) === 'web'
  const showCard = !isWeb && status !== null && !status.hasEntitlement
  const ctaLabel = status?.isAuthenticated
    ? t('settings.sync.entry.activate')
    : t('settings.sync.entry.setup')

  return (
    <div className="account-profile-page">
      <AccountProfileSection variant="card" />
      {showCard && (
        <HappyCloudEntryCard
          title={t('settings.sync.entry.title')}
          subtitle={t('settings.sync.entry.subtitle')}
          action={{ label: ctaLabel, onClick: onSetupSync, trailingArrow: true }}
        />
      )}
    </div>
  )
}
